import { useEffect, useState } from "react";
import { cn } from "~/../lib/utils";
import type { IconNodeMap } from "~/utils/lucide-icons";
import {
  getLoadedIconNodes,
  ICON_STROKE_WIDTH,
  ICON_VIEW_BOX,
  loadIconNodes,
  resolveIconNodes,
} from "~/utils/lucide-icons";

/**
 * The full Lucide set, loaded once and shared by every component that asks.
 *
 * Seeded from the module cache rather than always starting empty, so the second and every
 * later icon in a session renders on its first paint instead of flashing a placeholder.
 */
export function useIconNodes(): IconNodeMap | null {
  const [icons, setIcons] = useState<IconNodeMap | null>(getLoadedIconNodes);

  useEffect(() => {
    if (icons) return;

    let cancelled = false;
    loadIconNodes()
      .then((loaded) => {
        if (!cancelled) setIcons(loaded);
      })
      .catch((error) => {
        // Not fatal: every call site renders a blank box of the right size instead. An
        // icon is decoration on top of a value, a marker or a category — none of them
        // stop meaning what they mean because the glyph is missing.
        console.error("Failed to load Lucide icon data", error);
      });

    return () => {
      cancelled = true;
    };
  }, [icons]);

  return icons;
}

/**
 * Renders a Lucide icon chosen by *name* at runtime.
 *
 * For icons known at compile time, import the component from `lucide-react` instead —
 * that tree-shakes to a single icon, where this necessarily carries the whole set. See
 * the note at the top of `lucide-icons.ts`.
 *
 * An unknown name renders Lucide's `squircle` rather than nothing, because the name comes
 * from stored config: an icon deleted or renamed by a Lucide upgrade should look
 * obviously unset, not silently vanish.
 */
export function DynamicIcon({
  name,
  size = 24,
  color,
  strokeWidth = ICON_STROKE_WIDTH,
  className,
  title,
}: {
  /** A Lucide kebab-case id, e.g. `"house"` or `"trash-off"`. */
  name: string | null | undefined;
  size?: number;
  /** Any CSS colour. Left unset the icon inherits `currentColor`, which is what lets it
   * follow the surrounding text and the light/dark theme. */
  color?: string;
  strokeWidth?: number;
  className?: string;
  /** Gives the icon an accessible name. Without it the icon is decorative and hidden from
   * assistive tech, which is right when a visible label sits beside it. */
  title?: string;
}) {
  const icons = useIconNodes();
  const nodes = icons ? resolveIconNodes(icons, name) : null;

  // Holds its space while the set loads, so a grid of icons doesn't reflow underneath the
  // reader when the chunk arrives.
  if (!nodes) {
    return (
      <span
        aria-hidden
        className={cn("inline-block shrink-0", className)}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={ICON_VIEW_BOX}
      fill="none"
      stroke={color ?? "currentColor"}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn("shrink-0", className)}
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {title ? <title>{title}</title> : null}
      {nodes.map(([Tag, attributes], index) => {
        // Every attribute key in the dataset is a single lowercase word (`d`, `cx`, `x1`,
        // `points`, …), so React's DOM props and raw SVG attributes are spelled the same
        // and no translation is needed. Verified across all 1848 icons.
        const Element = Tag as keyof React.JSX.IntrinsicElements;
        return <Element key={index} {...attributes} />;
      })}
    </svg>
  );
}
